import dayjs from "dayjs";
import { GENDER_TYPE } from "../configs/constants/GenderType";
import { isCredentialValid } from "../utils/credential.utils";
import { ConsultantsListResponse } from "./Consultant";
import { NO_SUBSIDIARY } from "./Subsidiary";

/**
 * The weekday keys of a consultant's weekly availability schedule
 * (`profile.availability`). Order matches Monday-first display order.
 */
export const WEEKDAYS = [
	"monday",
	"tuesday",
	"wednesday",
	"thursday",
	"friday",
	"saturday",
	"sunday",
] as const;

export type Weekday = typeof WEEKDAYS[number];

/**
 * Canonical key for a consultant's home town. `profile.address.city` is free
 * text, so the filter compares on a trimmed, whitespace-collapsed, lower-cased
 * form — "Uppsala", "UPPSALA" and " uppsala " are one town. Returns undefined
 * for a missing/blank city so callers can treat it as "no town".
 */
export function cityKey(city?: string | null): string | undefined {
	const key = (city ?? "").trim().replace(/\s+/g, " ").toLocaleLowerCase("sv");
	return key || undefined;
}

/**
 * The days the "actually available" filter can constrain, as relative tokens
 * rather than dates: a saved filter or a shared URL must keep meaning "today"
 * whenever it is applied, not the day it was created. Resolved to concrete
 * dates by {@link resolveAvailabilityDates}.
 */
export const ACTUAL_AVAILABILITY_DAYS = ["today", "tomorrow"] as const;

export type ActualAvailabilityDay = typeof ACTUAL_AVAILABILITY_DAYS[number];

/** How many days after today each token is. */
const DAY_OFFSETS: Record<ActualAvailabilityDay, number> = {
	today: 0,
	tomorrow: 1,
};

/** The YYYY-MM-DD date each relative token refers to, in the browser's time zone. */
export function resolveAvailabilityDates(
	now: Date = new Date()
): Record<ActualAvailabilityDay, string> {
	const dates = {} as Record<ActualAvailabilityDay, string>;

	ACTUAL_AVAILABILITY_DAYS.forEach((day) => {
		dates[day] = dayjs(now).add(DAY_OFFSETS[day], "day").format("YYYY-MM-DD");
	});

	return dates;
}

/**
 * How a day may be constrained by the booked-availability filter: the
 * consultant must be free (no booked shift, no absence) or busy ("booked",
 * which includes absences — the busy-days lookup does not distinguish them).
 */
export type BookingState = "free" | "booked";

/**
 * Day → required {@link BookingState}. A day missing from the record is
 * unconstrained ("both"); an empty record is no constraint at all.
 */
export type ActualAvailabilitySelection = Partial<Record<ActualAvailabilityDay, BookingState>>;

/**
 * The `actualAvailability` group in its canonical record shape. Saved filters,
 * org filters and shared URLs from before the booked/free choice store the
 * legacy array shape (`["today"]` = free on that day), which must keep meaning
 * what it meant when saved — normalize wherever the group is read.
 */
export function getActualAvailability(def: FilterDefinition): ActualAvailabilitySelection {
	const raw = def.actualAvailability;
	if (!raw) return {};
	if (Array.isArray(raw)) {
		const normalized: ActualAvailabilitySelection = {};
		raw.forEach((day) => (normalized[day] = "free"));
		return normalized;
	}
	return raw;
}

/**
 * One competence criterion in the hierarchy branch (industry) → service →
 * skills. A criterion may stop at any level: branch only, branch+service, or
 * the full path. Matching is "same-entry": a consultant satisfies it only if
 * ONE of their competence entries matches branch (and service, and any of the
 * skills) — see matchesDefinition.
 */
export interface CompetenceSelection {
	branch: string;
	service?: string;
	skills?: string[];
}

/**
 * A structured, client-side filter over the in-memory consultant list.
 *
 * Semantics: AND across groups. Within a multi-select group: OR for
 * single-valued attributes (gender, manager, OU), but AND for certifications
 * and tags — the consultant must hold ALL selected ones (they are additive
 * credentials, and filtering is used to find fully qualified consultants).
 * An omitted / empty group is "no constraint". This is what the filter panel
 * edits and what a saved filter / built-in preset stores.
 */
export interface FilterDefinition {
	/** undefined = both active and inactive */
	status?: "active" | "inactive";
	/** competence criteria; matched per competenceMatch */
	competenceSelections?: CompetenceSelection[];
	/** how to combine the competence criteria. Defaults to "any" (OR). */
	competenceMatch?: "any" | "all";
	genders?: GENDER_TYPE[];
	managerIds?: string[];
	/**
	 * home towns (`profile.address.city`, OR within the group), stored as
	 * {@link cityKey}s so "Uppsala" and "UPPSALA " count as the same town.
	 */
	cities?: string[];
	/**
	 * subsidiary ids (OR within the group). The NO_SUBSIDIARY sentinel matches
	 * consultants connected to the organisation directly (no subsidiary).
	 */
	subsidiaryIds?: string[];
	/** certification typeKeys (org `certificationTypes`); only VALID (unexpired) entries count */
	certifications?: string[];
	/** consultant tag keys (org `consultantTagTypes`); only VALID (unexpired) entries count */
	tags?: string[];
	/**
	 * weekday keys (see {@link WEEKDAYS}) the consultant must have availability on.
	 * AND within the group: a match needs at least one availability timeframe on
	 * EVERY selected day. Requires the list to be fetched with includeAvailability.
	 */
	availabilityDays?: Weekday[];
	/**
	 * Booking-state requirement per relative day (see
	 * {@link ActualAvailabilitySelection}): "free" = no booked shift and no
	 * absence, "booked" = busy. Unlike `availabilityDays`, which is the
	 * consultant's own stated weekly schedule, this is their actual booking
	 * situation. AND within the group: every constrained day must hold.
	 *
	 * The legacy array shape (`["today"]` = free on that day) still occurs in
	 * saved/org filters and shared URLs — read via {@link getActualAvailability}.
	 *
	 * Evaluated against {@link MatchContext.busyDatesByConsultant}, which the
	 * caller fetches separately — see `useBookedConsultantIds`.
	 */
	actualAvailability?: ActualAvailabilitySelection | ActualAvailabilityDay[];
}

/**
 * Data that a definition needs but a consultant row does not carry, supplied by
 * the caller of {@link matchesDefinition}.
 */
export interface MatchContext {
	/**
	 * consultantId → the YYYY-MM-DD dates they are busy on (booked shift or
	 * absence). Only busy consultants are present; a missing entry means free.
	 */
	busyDatesByConsultant: Map<string, Set<string>>;
	/** What each relative token resolves to, from {@link resolveAvailabilityDates}. */
	availabilityDates: Record<ActualAvailabilityDay, string>;
}

export interface SavedFilter {
	id: string;
	name: string;
	definition: FilterDefinition;
}

export const EMPTY_DEFINITION: FilterDefinition = {};

const FILTER_GROUPS: (keyof FilterDefinition)[] = [
	"status",
	"competenceSelections",
	"genders",
	"managerIds",
	"cities",
	"subsidiaryIds",
	"certifications",
	"tags",
	"availabilityDays",
	"actualAvailability",
];

/** A definition with no active constraints (the "All" preset). */
export function isEmptyDefinition(def: FilterDefinition): boolean {
	return FILTER_GROUPS.every((key) => {
		const value = def[key];
		if (Array.isArray(value)) return value.length === 0;
		// actualAvailability's record shape: no constrained day = no constraint.
		if (typeof value === "object" && value !== null) return Object.keys(value).length === 0;
		return value === undefined;
	});
}

/**
 * Canonical JSON for a definition so two definitions that differ only in array
 * ordering or empty/omitted groups compare equal. Used to tell which pill (if
 * any) the current filter corresponds to.
 */
function canonicalize(def: FilterDefinition): string {
	const normalized: Record<string, unknown> = {};

	FILTER_GROUPS.forEach((key) => {
		const value = def[key];
		if (key === "competenceSelections" && Array.isArray(value)) {
			if (value.length)
				normalized[key] = (value as CompetenceSelection[])
					.map((s) =>
						JSON.stringify({
							branch: s.branch,
							service: s.service,
							skills: s.skills ? [...s.skills].sort() : undefined,
						})
					)
					.sort();
		} else if (key === "actualAvailability") {
			// Normalize so a legacy `["today"]` compares equal to `{today: "free"}` —
			// otherwise pre-existing org/saved filter pills would stop lighting up.
			const entries = Object.entries(getActualAvailability(def)).sort();
			if (entries.length) normalized[key] = entries.map(([day, state]) => `${day}:${state}`);
		} else if (Array.isArray(value)) {
			if (value.length) normalized[key] = [...value].sort();
		} else if (value !== undefined) {
			normalized[key] = value;
		}
	});

	// The match mode only matters when there are competence criteria; ignore the
	// default ("any") so it doesn't make an otherwise-preset definition "custom".
	if (def.competenceSelections?.length && def.competenceMatch === "all")
		normalized.competenceMatch = "all";

	return JSON.stringify(normalized);
}

export function definitionsEqual(a: FilterDefinition, b: FilterDefinition): boolean {
	return canonicalize(a) === canonicalize(b);
}

/**
 * Whether a single consultant row satisfies the definition. `context` carries
 * the data that lives outside the row (booked days); a definition that needs it
 * matches nothing when it is missing.
 */
export function matchesDefinition(
	c: ConsultantsListResponse,
	def: FilterDefinition,
	context?: MatchContext
): boolean {
	if (def.status === "active" && !c.active) return false;
	if (def.status === "inactive" && c.active) return false;

	if (def.genders?.length) {
		const gender = c.profile?.gender;
		if (!gender || !def.genders.includes(gender)) return false;
	}

	if (def.managerIds?.length) {
		const managerId = c.profile?.manager?.id;
		if (!managerId || !def.managerIds.includes(managerId)) return false;
	}

	if (def.cities?.length) {
		const key = cityKey(c.profile?.address?.city);
		if (!key || !def.cities.includes(key)) return false;
	}

	// OR within the group; NO_SUBSIDIARY matches consultants without one.
	if (def.subsidiaryIds?.length) {
		const subsidiaryId = c.profile?.subsidiaryId;
		const matched = subsidiaryId
			? def.subsidiaryIds.includes(subsidiaryId)
			: def.subsidiaryIds.includes(NO_SUBSIDIARY);
		if (!matched) return false;
	}

	// AND within the group: the consultant must hold EVERY selected
	// certification as a VALID (unexpired) entry. Expired entries never
	// satisfy a filter.
	if (def.certifications?.length) {
		const held = c.profile?.certifications ?? [];
		const hasAll = def.certifications.every((typeKey) =>
			held.some((cert) => cert.typeKey === typeKey && isCredentialValid(cert.expiresAt))
		);
		if (!hasAll) return false;
	}

	if (def.tags?.length) {
		const held = c.profile?.tags ?? [];
		const hasAll = def.tags.every((key) =>
			held.some((tag) => tag.key === key && isCredentialValid(tag.expiresAt))
		);
		if (!hasAll) return false;
	}

	if (def.competenceSelections?.length) {
		const entries = c.profile?.competences ?? [];
		const all = def.competenceMatch === "all";

		// Whether the consultant satisfies one criterion (same-entry: branch, the
		// service if set, and the skill). For a multi-skill criterion, "all" mode
		// requires every skill present (each in some entry), "any" mode just one.
		const matchSelection = (sel: CompetenceSelection): boolean => {
			if (sel.skills?.length) {
				const hasSkill = (skill: string) =>
					entries.some(
						(e) =>
							e.branch === sel.branch &&
							(!sel.service || e.service === sel.service) &&
							e.skills?.includes(skill)
					);
				return all ? sel.skills.every(hasSkill) : sel.skills.some(hasSkill);
			}
			if (sel.service)
				return entries.some((e) => e.branch === sel.branch && e.service === sel.service);
			return entries.some((e) => e.branch === sel.branch);
		};

		const matched = all
			? def.competenceSelections.every(matchSelection)
			: def.competenceSelections.some(matchSelection);
		if (!matched) return false;
	}

	// AND within the group: the consultant needs at least one availability
	// timeframe on EVERY selected weekday. A consultant with no configured
	// availability never matches.
	if (def.availabilityDays?.length) {
		const availability = c.profile?.availability;
		const availableOnAll =
			!!availability && def.availabilityDays.every((day) => (availability[day]?.length ?? 0) > 0);
		if (!availableOnAll) return false;
	}

	// AND within the group: every constrained day must hold ("free" = not busy,
	// "booked" = busy). Without the booked-days lookup the constraint can't be
	// honoured, so nothing matches — showing a booked consultant as available is
	// the one wrong answer here.
	const actualAvailability = Object.entries(getActualAvailability(def)) as [
		ActualAvailabilityDay,
		BookingState
	][];
	if (actualAvailability.length) {
		if (!context) return false;

		const busyDates = context.busyDatesByConsultant.get(c.id);

		const holdsOnAll = actualAvailability.every(([day, state]) => {
			const busy = busyDates?.has(context.availabilityDates[day]) ?? false;
			return state === "booked" ? busy : !busy;
		});
		if (!holdsOnAll) return false;
	}

	return true;
}
